import { useEffect, useRef, useState } from 'react';
import { usePowerSyncDb } from '../adapters/powersync';
import { useUploadQueueStats } from './useUploadQueueStats';
import {
  deriveSyncHealthState,
  getRecentUploadErrors,
  isAuthExpiredSyncError,
  isReopenLoopError,
  SYNC_REOPEN_STUCK_MS,
  type SyncHealthState,
  type LocalUploadErrorEntry,
} from './syncHealth';

export interface SyncStatus {
  /** Derived high-level health state. */
  state: SyncHealthState;
  /** Live PowerSync connection flag. */
  connected: boolean;
  connecting: boolean;
  /** Actively downloading or uploading right now. */
  inFlight: boolean;
  /** Whether an initial full sync has completed at least once. */
  hasSynced: boolean;
  /** Last successful sync as a locale string (for display), or null. */
  lastSyncedAt: string | null;
  /** Raw ISO timestamp of last sync (for the bundle), or null. */
  lastSyncedAtIso: string | null;
  /** Pending upload op count. */
  pendingCount: number;
  /** Estimated serialized size of the pending queue, in bytes. */
  estimatedSizeBytes: number;
  /** Most recent captured upload error message (summary only), or null. */
  lastErrorMessage: string | null;
  /** Recent captured upload errors (newest first). */
  recentErrors: LocalUploadErrorEntry[];
  /** Browser network reachability. */
  online: boolean;
  /**
   * The session token expired (PSYNC_S2103) so the sync stream closed — the
   * device is no longer receiving updates and may be showing stale data.
   * True only while NOT connected, so it clears the moment sync recovers.
   */
  authExpired: boolean;
  /**
   * Sync is stuck in a re-open loop (typically after a sync-rules change) and
   * hasn't settled for SYNC_REOPEN_STUCK_MS. Recovery is a local clear +
   * re-sync. Cleared the moment the loop stops.
   */
  downloadStuck: boolean;
}

const POLL_INTERVAL_MS = 2_000;

/**
 * PRD.253 §7.1 — `useSyncStatus()`.
 *
 * Combines PowerSync's live status, the upload queue, captured local errors,
 * and the browser online flag into a single derived health state.
 */
export function useSyncStatus(): SyncStatus {
  const { db } = usePowerSyncDb();
  const queue = useUploadQueueStats();

  const [snapshot, setSnapshot] = useState<{
    connected: boolean;
    connecting: boolean;
    inFlight: boolean;
    hasSynced: boolean;
    lastSyncedAt: string | null;
    lastSyncedAtIso: string | null;
    syncErrorMessage: string | null;
  }>({
    connected: false,
    connecting: false,
    inFlight: false,
    hasSynced: false,
    lastSyncedAt: null,
    lastSyncedAtIso: null,
    syncErrorMessage: null,
  });

  const [online, setOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [recentErrors, setRecentErrors] = useState<LocalUploadErrorEntry[]>(() =>
    getRecentUploadErrors(),
  );
  // Timestamp (ms) when the current "stuck" condition started, or null when
  // healthy. Persisted across polls so we can require it to last a while before
  // flagging stuck (avoids tripping on normal brief reconnects / first sync).
  const stuckSinceRef = useRef<number | null>(null);

  // Track browser online/offline.
  useEffect(() => {
    function update() {
      setOnline(navigator.onLine);
    }
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  // Poll PowerSync status + re-read captured errors.
  useEffect(() => {
    if (!db) return;

    function update() {
      const s = db!.currentStatus;
      const lastSyncedDate = s?.lastSyncedAt ? new Date(s.lastSyncedAt) : null;
      const downloadErr = s?.dataFlowStatus?.downloadError?.message ?? null;
      const uploadErr = s?.dataFlowStatus?.uploadError?.message ?? null;
      setSnapshot({
        connected: s?.connected ?? false,
        connecting: s?.connecting ?? false,
        inFlight: (s?.dataFlowStatus?.downloading ?? false) || (s?.dataFlowStatus?.uploading ?? false),
        hasSynced: s?.hasSynced ?? false,
        lastSyncedAt: lastSyncedDate ? lastSyncedDate.toLocaleString() : null,
        lastSyncedAtIso: lastSyncedDate ? lastSyncedDate.toISOString() : null,
        // Active sync error reported by PowerSync (download/websocket or upload).
        syncErrorMessage: downloadErr || uploadErr || null,
      });
      setRecentErrors(getRecentUploadErrors());

      // Track when the "stuck" condition started so we can age it into a flag.
      // Two signals (ref.needhelp §12.3):
      //   1. A re-open loop error (ConnectionClosedError / "busy re-opening").
      //   2. Invalid checkpoint — hasSynced=true but no lastSyncedAt. This is
      //      the exact state seen when a sync-rules change leaves the client
      //      unable to finalize a checkpoint ("Last synced: Never" despite
      //      having synced before). It's a cleaner state signal than the error
      //      string and survives the connect/disconnect flapping.
      // A real successful sync sets lastSyncedAt, clearing both → resets.
      const looping = isReopenLoopError(downloadErr) || isReopenLoopError(uploadErr);
      const invalidCheckpoint = s?.hasSynced === true && !lastSyncedDate;
      if (looping || invalidCheckpoint) {
        if (stuckSinceRef.current == null) stuckSinceRef.current = Date.now();
      } else {
        stuckSinceRef.current = null;
      }
    }

    update();
    const interval = setInterval(update, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [db]);

  // Prefer a captured upload error; fall back to the live PowerSync sync error
  // (e.g. a websocket/download failure that never produced an upload error).
  const lastErrorMessage = recentErrors[0]?.error?.message ?? snapshot.syncErrorMessage ?? null;

  const state = deriveSyncHealthState({
    connected: snapshot.connected,
    online,
    inFlight: snapshot.inFlight,
    pendingCount: queue.count,
    oldestPendingAt: queue.oldestPendingAt,
    lastSyncedAt: snapshot.lastSyncedAtIso,
    recentErrorCount: recentErrors.length,
    hasSyncError: Boolean(snapshot.syncErrorMessage),
  });

  // Session/JWT expiry: the sync stream closed with an expired-auth error and
  // we're not currently connected. Surfaced separately so the app can prompt a
  // reconnect even when there are zero pending uploads (download-only staleness).
  const authExpired = !snapshot.connected && isAuthExpiredSyncError(snapshot.syncErrorMessage);

  // Stuck only once the condition has persisted past the threshold, so a normal
  // brief reconnect or the first-sync window never trips it.
  const downloadStuck =
    online &&
    stuckSinceRef.current != null &&
    Date.now() - stuckSinceRef.current >= SYNC_REOPEN_STUCK_MS;

  return {
    state,
    connected: snapshot.connected,
    connecting: snapshot.connecting,
    inFlight: snapshot.inFlight,
    hasSynced: snapshot.hasSynced,
    lastSyncedAt: snapshot.lastSyncedAt,
    lastSyncedAtIso: snapshot.lastSyncedAtIso,
    pendingCount: queue.count,
    estimatedSizeBytes: queue.estimatedSizeBytes,
    lastErrorMessage,
    recentErrors,
    online,
    authExpired,
    downloadStuck,
  };
}
